<script lang="ts">
  import type { HTMLInputAttributes, HTMLInputTypeAttribute } from "svelte/elements";

  interface Props extends Omit<HTMLInputAttributes, "type" | "value" | "bind:value" | "oninput"> {
    value?: string;
    type?: Exclude<HTMLInputTypeAttribute, "number" | "range">;
    oninput?: HTMLInputAttributes["oninput"];
    density?: "normal" | "compact" | "comfortable";
    leadingIcon?: boolean;
    trailingAction?: boolean;
    flexible?: boolean;
  }

  let {
    value = $bindable<string>(""),
    type = "text",
    oninput,
    density = "normal",
    leadingIcon = false,
    trailingAction = false,
    flexible = false,
    ...attributes
  }: Props = $props();
</script>

<input
  class:compact={density === "compact"}
  class:comfortable={density === "comfortable"}
  class:with-leading-icon={leadingIcon}
  class:with-trailing-action={trailingAction}
  class:flexible
  {type}
  value={value ?? ""}
  oninput={(event) => {
    value = event.currentTarget.value;
    oninput?.(event);
  }}
  {...attributes}
/>

<style>
  input {
    box-sizing: border-box;
    width: 100%;
    min-height: 2.8rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--line-strong);
    border-radius: 0;
    outline: none;
    color: var(--cream);
    background: var(--ink);
    font: inherit;
    font-size: 0.86rem;
    transition:
      border-color 160ms ease,
      box-shadow 160ms ease;
  }

  input::placeholder {
    color: var(--dim);
  }
  input:focus-visible {
    border-color: var(--mars-light);
    box-shadow: 0 0 0 3px rgb(214 75 50 / 0.08);
  }
  input:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  input.compact {
    min-height: 2.55rem;
    padding: 0.5rem 0.65rem;
  }
  input.comfortable {
    min-height: 3rem;
    padding: 0.75rem 0.85rem;
  }
  input.with-leading-icon {
    padding-left: 2.6rem;
  }
  input.with-trailing-action {
    padding-right: 2.4rem;
  }
  input.flexible {
    min-width: 0;
    flex: 1;
  }
</style>
